import React, { useState, useMemo, useEffect } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as tickerLogosSelectors from 'app-components/flux/ticker-logos/tickerLogosSelectors';
import { tracker } from 'app-components/utils/core';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Avatar from '@mui/material/Avatar';
import type { AvatarProps } from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';

import { useAppSelector } from 'hooks/redux';

const medium = 32;
const large = 40;

export type StockLogoProps = AvatarProps & {
  type?: 'stock' | 'cash';
  value?: string;
  size?: 'medium' | 'large';
};

export const TickerAvatar = (props: StockLogoProps) => {
  const { value, size, type = 'stock', sx, slotProps, ...otherProps } = props;
  const theme = useTheme();
  const [error, setError] = useState(false);
  const tickerLogo = useAppSelector((state) => tickerLogosSelectors.getTickerLogoByKey(state, value));
  const sxOverride = useMemo(() => {
    if (size === 'medium') {
      return { width: medium, height: medium, ...sx };
    } else if (size === 'large') {
      return { width: large, height: large, ...sx };
    }
    return sx;
  }, [size, sx]);

  const trackLogoTicker = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'trackLogoTicker'));
  const tickerValidate = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'tickerValidate'));
  const valid = useMemo(() => Boolean(value?.match(tickerValidate || '')), [tickerValidate, value]);
  useEffect(() => {
    if (trackLogoTicker && value && !tickerLogo?.logoUrl) {
      tracker.track(tracker.events.logoTicker, { ticker: value, valid, url: tickerLogo?.logoUrl, status: 'Not Found' });
    }
  }, [tickerLogo?.logoUrl, value, trackLogoTicker, valid]);

  return type === 'cash' ? (
    <Avatar
      sharedcomponentid='TickerAvatar'
      id={`stock-logo-${value}`}
      alt={`${value} logo`}
      variant='rounded'
      sx={sxOverride}
      {...otherProps}
    >
      <AttachMoneyIcon color='success' />
    </Avatar>
  ) : error || !tickerLogo?.logoUrl ? (
    <Avatar
      sharedcomponentid='TickerAvatar'
      id={`stock-logo-${value}`}
      alt={`${value} logo`}
      variant='rounded'
      sx={sxOverride}
      {...otherProps}
    >
      <TrendingUpIcon />
    </Avatar>
  ) : (
    <Avatar
      sharedcomponentid='TickerAvatar'
      id={`stock-logo-${value}`}
      alt={`${value} logo`}
      variant='rounded'
      src={tickerLogo?.logoUrl}
      onError={() => {
        if (trackLogoTicker) {
          tracker.track(tracker.events.logoTicker, {
            ticker: value,
            valid,
            url: tickerLogo?.logoUrl,
            status: 'Failed',
          });
        }
        setError(true);
      }}
      onLoad={() => {
        if (trackLogoTicker) {
          tracker.track(tracker.events.logoTicker, {
            ticker: value,
            valid,
            url: tickerLogo?.logoUrl,
            status: 'Succeed',
          });
        }
        setError(false);
      }}
      sx={sxOverride}
      slotProps={{
        img: {
          sx: {
            objectFit: 'contain',
            backgroundColor: theme.palette.common.white,
          },
          ...slotProps?.img,
        },
        ...slotProps,
      }}
      {...otherProps}
    />
  );
};

export default TickerAvatar;
